/**
 * modulesMatrix
 * ------------------
 * This matrix defines all available My Account items (tiles/cards) shown
 * in the "My Account" section, including metadata, access rules, and dynamic conditions.
 *
 * Each entry specifies:
 * - `id`: Unique identifier for internal use
 * - `allowedTenants`: List of country/tenant codes where the item is available.
 *   Use ["*"] for global availability.
 * - `allowedRoles`: List of user roles that can see the item.
 *   Use [] for no restrictions.
 * - `nameKey`, `descriptionKey`, `linkTextKey`: i18n translation keys
 * - `link`: Route object or function returning a route
 * - `showIf`: Optional function receiving a `conditionContext` to dynamically evaluate visibility
 *
 * This matrix is used in combination with tenant and role-based filters to compute
 * the final list of visible items per user.
 */
export const modulesMatrix = [
  {
    id: "myProfile",
    allowedRoles: ["dealer"],
    nameKey: "account.categories.baseData.title",
    descriptionKey: "account.categories.baseData.desc",
    link: { name: "stammdaten" },
    linkTextKey: "account.buttons.userProfile",
    myAccount: true,
    showIf: (s) => s.canSeeProfile(),
  },
  {
    id: "mailbox",
    allowedRoles: ["dealer", "staff", "wholesaler", "wholesalerStaff"],
    nameKey: "mailbox.title",
    descriptionKey: "account.categories.mailbox.desc",
    link: { name: "mailbox" },
    linkTextKey: "account.buttons.mailbox",
    myAccount: true,
    showIf: (s) => s.canSeeMailbox(),
  },
  {
    id: "bankStatement",
    allowedRoles: ["dealer"],
    nameKey: "account.categories.bankstatement.title",
    descriptionKey: "account.categories.bankstatement.desc",
    link: { name: "pointsOverview" },
    linkTextKey: "global.buttons.pointsList",
    myAccount: true,
    showIf: (s) => s.canSeePointsInfo() && !["FR", "BE"].includes(String(s.tenant || "").toUpperCase()),
  },
  {
    id: "staff",
    allowedRoles: ["staff"],
    nameKey: "staff.dashboard.title",
    descriptionKey: "staff.dashboard.short",
    link: () => ({ name: "staff" }),
    linkTextKey: "global.buttons.toStaffDashboard",
    myAccount: true,
    showIf: (s) => s.canSeeStaff(),
  },

  {
    id: "wholesaler",
    allowedRoles: ["wholesaler", "wholesalerStaff"],
    nameKey: "staff.dashboard.title",
    descriptionKey: "staff.dashboard.short",
    link: () => ({ name: "wholesaler" }),
    linkTextKey: "global.buttons.toStaffDashboard",
    myAccount: true,
    showIf: (s) => s.canSeeWholesaler(),
  },
  {
    id: "vouchers",
    allowedRoles: ["staff", "wholesaler", "wholesalerStaff"],
    nameKey: "vouchers.title",
    descriptionKey: "vouchers.short_description",
    link: (s) => {
      const role = s.userRoles?.includes("staff")
        ? "staffVouchers"
        : "wholesalerVouchers";
      return { name: role };
    },
    linkTextKey: "global.buttons.vouchers",
    myAccount: true,
    showIf: (s) => s.canSeeVouchers(),
  },
  {
    id: "kickbackInfo",
    allowedRoles: ["dealer", "staff"],
    nameKey: "kickbackInfo.title",
    descriptionKey: "kickbackInfo.short_description",
    link: { name: "rewardsOverview" },
    linkTextKey: "buttons.toKickbackFeeInfo",
    myAccount: true,
    showIf: (s) => s.canSeeKickbackInfo(),
  },
  {
    id: "kickbackPayout",
    allowedRoles: [],
    nameKey: "global.nav.payouts",
    descriptionKey: "global.payoutsDescription",
    link: { name: "payouts" },
    linkTextKey: "global.nav.payouts",
    myAccount: true,
    showIf: (s) => s.canSeeKickbackPayout(),
  },
  {
    id: "userOrders",
    allowedRoles: ["dealer"],
    nameKey: "shop.orders",
    descriptionKey: "shop.order.description",
    link: { name: "userOrders" },
    linkTextKey: "global.buttons.userOrders",
    myAccount: true,
    showIf: (s) => s.canSeeShopOrders(),
  },
  {
    id: "favoriteProducts",
    allowedRoles: ["dealer"],
    nameKey: "account.categories.favoriteProducts.title",
    descriptionKey: "account.categories.favoriteProducts.desc",
    link: { name: "favoriteProducts" },
    linkTextKey: "account.buttons.favoriteProducts",
    myAccount: true,
    showIf: (s) => s.canSeeFavProducts(),
  },
  {
    id: "commitmentLetter",
    allowedRoles: ["dealer"],
    nameKey: "commitmentLetter.title",
    descriptionKey: "commitmentLetter.short_description",
    link: { name: "commitment_letter" },
    linkTextKey: "global.buttons.toCommitmentLetter",
    myAccount: true,
    showIf: (s) => s.canSeeCommitmentLetter(),
  },
  {
    id: "firstStop",
    allowedRoles: ["dealer"],
    nameKey: "about.sections.firstStop.title",
    descriptionKey: "about.sections.firstStop.description",
    link: { name: "first_stop" },
    linkTextKey: "buttons.toKickbackFeeInfo",
    myAccount: true,
    showIf: (s) => s.isFirstStopGroup(),
  },
  {
    id: "promotions",
    allowedRoles: ["dealer"],
    nameKey: "promotions.accountBox.title",
    descriptionKey: "promotions.accountBox.description",
    link: { name: "promotions" },
    linkTextKey: "promotions.accountBox.button",
    myAccount: true,
    showIf: (s) => s.canSeePromotions(),
  },
  {
    id: "apps",
    allowedRoles: ["dealer"],
    nameKey: "account.apps.title",
    descriptionKey: "account.apps.description",
    link: { name: "apps" },
    linkTextKey: "buttons.toApps",
    myAccount: false,
    showIf: (s) => s.canSeeApps(),
  },
  ///
  {
    id: "news",
    allowedRoles: ["dealer"],
    nameKey: "account.news.title",
    descriptionKey: "account.news.description",
    link: { name: "promotions" },
    linkTextKey: "buttons.toApps",
    myAccount: false,
    showIf: (s) => s.canSeeNews(),
  },
  {
    id: "teasers",
    allowedRoles: ["dealer"],
    nameKey: "account.teasers.title",
    descriptionKey: "account.teasers.description",
    link: { name: "promotions" },
    linkTextKey: "buttons.toApps",
    myAccount: false,
    showIf: (s) => s.canSeeTeaser(),
  },
  {
    id: "slideshow",
    allowedRoles: ["dealer"],
    nameKey: "account.slideshow.title",
    descriptionKey: "account.slideshow.description",
    link: { name: "promotions" },
    linkTextKey: "buttons.toApps",
    myAccount: false,
    showIf: (s) => s.canSeeSlideshow(),
  },
  {
    id: "mainBoxes",
    allowedRoles: ["dealer"],
    nameKey: "account.mainBoxes.title",
    descriptionKey: "account.mainBoxes.description",
    link: { name: "promotions" },
    linkTextKey: "buttons.toApps",
    myAccount: false,
    showIf: (s) => s.canSeeMainBox(),
  },
  {
    id: "frontendMenu",
    allowedRoles: ["dealer"],
    nameKey: "account.frontendMenu.title",
    descriptionKey: "account.frontendMenu.description",
    link: { name: "promotions" },
    linkTextKey: "buttons.toApps",
    myAccount: false,
    showIf: (s) => s.canSeeFrontendMenu(),
  },
  {
    id: "helpWithLinkTooltip",
    allowedRoles: ["dealer"],
    nameKey: "account.helpWithLinkTooltip.title",
    descriptionKey: "account.helpWithLinkTooltip.description",
    link: { name: "promotions" },
    linkTextKey: "buttons.toApps",
    myAccount: false,
    showIf: (s) => s.canSeeHelpWithLinkTooltip(),
  },
  {
    id: "helpTooltip",
    allowedRoles: ["dealer"],
    nameKey: "account.helpTooltip.title",
    descriptionKey: "account.helpTooltip.description",
    link: { name: "promotions" },
    linkTextKey: "buttons.toApps",
    myAccount: false,
    showIf: (s) => s.canSeeHelpTooltip(),
  },
];
